import Dexie, {type Table} from 'dexie';
import {gzip} from 'pako';

export const REPLAY_OUTBOX_MAX_EVENTS = 200;
export const REPLAY_OUTBOX_TARGET_BYTES = 1024 * 1024;
export const REPLAY_OUTBOX_MAX_EVENT_BYTES = 64 * 1024 * 1024;
export const REPLAY_OUTBOX_MAX_UPLOAD_BYTES = 64 * 1024 * 1024;
export const REPLAY_SESSION_SOFT_CHUNK_SLOTS = 4096;
// Leave room below the server's 250 MiB cap for one exceptional 64 MiB event
// and gzip framing while the capture queue prepares the next part.
export const REPLAY_SESSION_SOFT_BYTES = 128 * 1024 * 1024;

export interface ReplayOutboxScope {
    firmId: string;
    userId: string;
    sessionId: string;
}

export interface ReplayChunkAcknowledgement {
    protocolVersion: 2;
    sessionId: string;
    chunkIndex: number;
    digest: string;
    eventCount: number;
    bytesCompressed: number;
}

export interface PreparedReplayChunk extends ReplayChunkAcknowledgement {
    firstSequence: number;
    lastSequence: number;
    bytesSerialized: number;
    startTimestamp: number | null;
    endTimestamp: number | null;
    bytes: Uint8Array<ArrayBuffer>;
}

type ErrorCode = 'invalid-scope' | 'invalid-event' | 'batch-limit' | 'event-too-large'
    | 'chunk-too-large' | 'storage' | 'encoding' | 'integrity';

/** Rejected appends were never accepted; a failed prepare/ack never removes pending data. */
export class ReplayOutboxError extends Error {
    constructor(readonly code: ErrorCode, message: string, readonly cause?: unknown) {
        super(message);
        this.name = 'ReplayOutboxError';
    }
}

interface SessionRow {
    scopeKey: string;
    nextSequence: number;
    acknowledgedThrough: number;
    nextChunkIndex: number;
    pendingBytesSerialized?: number;
}
interface EventHeader { scopeKey: string; sequence: number; bytesSerialized: number; timestamp?: number | null }
interface EventBody { scopeKey: string; sequence: number; json: string }
interface ChunkRow extends PreparedReplayChunk { scopeKey: string }

const encoder = new TextEncoder();

interface ReplayPackingHeader {sequence: number; bytesSerialized: number}

/** One authoritative greedy packing rule for both preparation and capacity.
 * A single exceptional event may exceed the target; every later event starts
 * the next slot. Sequence gaps are rejected instead of silently undercounted. */
export function replayChunkSlotGroups(headers: readonly ReplayPackingHeader[], firstSequence: number): number[][] {
    const groups: number[][] = [];
    let selected: number[] = [];
    let bytesSerialized = 2;
    let expectedSequence = firstSequence;
    for (const header of headers) {
        if (!Number.isSafeInteger(header.sequence) || header.sequence !== expectedSequence ||
            !Number.isSafeInteger(header.bytesSerialized) || header.bytesSerialized < 0) {
            throw new ReplayOutboxError('integrity', 'Replay sequence is incomplete; pending records retained.');
        }
        const nextBytes = bytesSerialized + header.bytesSerialized + (selected.length ? 1 : 0);
        if (selected.length && (selected.length === REPLAY_OUTBOX_MAX_EVENTS || nextBytes > REPLAY_OUTBOX_TARGET_BYTES)) {
            groups.push(selected);
            selected = [];
            bytesSerialized = 2;
        }
        bytesSerialized += header.bytesSerialized + (selected.length ? 1 : 0);
        selected.push(header.sequence);
        expectedSequence++;
    }
    if (selected.length) groups.push(selected);
    return groups;
}

/** Dedicated storage: never upgrades, clears, or falls back to the application queue DB. */
class OutboxDatabase extends Dexie {
    sessions!: Table<SessionRow, string>;
    headers!: Table<EventHeader, [string, number]>;
    bodies!: Table<EventBody, [string, number]>;
    chunks!: Table<ChunkRow, string>;

    constructor(name: string) {
        super(name);
        this.version(1).stores({
            sessions: 'scopeKey',
            headers: '[scopeKey+sequence]',
            bodies: '[scopeKey+sequence]',
            chunks: 'scopeKey',
        });
    }
}

function publicChunk(row: ChunkRow): PreparedReplayChunk {
    const {scopeKey: _scopeKey, ...chunk} = row;
    // IDB already clones on read; also isolate concurrent callers of prepare().
    return {...chunk, bytes: new Uint8Array(chunk.bytes)};
}

/**
 * Durable storage only: callers supply already-masked events, and own authentication,
 * recorder backpressure, upload retries and tab leases. No credentials or network access.
 * A resolved append is committed to IndexedDB, subject to browser storage eviction policy.
 * Each append is atomic and bounded; callers must await it before considering events saved.
 */
export class SessionReplayOutbox {
    private readonly db: OutboxDatabase;
    private readonly scopeKey: string;
    private readonly sessionId: string;

    constructor(scope: ReplayOutboxScope, databaseName = 'SessionReplayOutboxV2') {
        if (!scope || [scope.firmId, scope.userId, scope.sessionId].some(value =>
            typeof value !== 'string' || !value.trim())) {
            throw new ReplayOutboxError('invalid-scope', 'Replay storage requires a firm, user and session.');
        }
        // Copy primitive identity now; changing the caller's scope cannot retarget a handle.
        this.scopeKey = JSON.stringify([scope.firmId, scope.userId, scope.sessionId]);
        this.sessionId = scope.sessionId;
        this.db = new OutboxDatabase(databaseName);
    }

    close(): void { this.db.close(); }

    private async stored<T>(operation: () => Promise<T>): Promise<T> {
        try { return await operation(); }
        catch (cause) {
            if (cause instanceof ReplayOutboxError) throw cause;
            throw new ReplayOutboxError('storage', 'Replay storage failed; pending records have been retained.', cause);
        }
    }

    /** At most 200 events / 1 MiB JSON array, or one event up to 64 MiB serialized. */
    async append(events: readonly unknown[]): Promise<{firstSequence: number; lastSequence: number}> {
        if (!Array.isArray(events) || !events.length || events.length > REPLAY_OUTBOX_MAX_EVENTS) {
            throw new ReplayOutboxError('batch-limit', 'Append requires 1–200 events.');
        }
        let bytesSerialized = 2;
        const serialized = events.map((event, index) => {
            let json: string;
            try {
                json = JSON.stringify(event);
                if (typeof json !== 'string') throw new Error('Event is not JSON serializable.');
            }
            catch (cause) { throw new ReplayOutboxError('invalid-event', 'Replay event could not be serialized.', cause); }
            const bytes = encoder.encode(json).byteLength;
            if (bytes > REPLAY_OUTBOX_MAX_EVENT_BYTES) {
                throw new ReplayOutboxError('event-too-large', 'Replay event exceeds the 64 MiB serialized limit.');
            }
            bytesSerialized += bytes + (index ? 1 : 0);
            if (events.length > 1 && bytesSerialized > REPLAY_OUTBOX_TARGET_BYTES) {
                throw new ReplayOutboxError('batch-limit', 'Replay append exceeds the 1 MiB batch target; split the batch.');
            }
            return {json, bytesSerialized: bytes};
        });
        return this.stored(() => this.db.transaction('rw', this.db.sessions, this.db.headers, this.db.bodies, async () => {
            const state = await this.db.sessions.get(this.scopeKey) ?? {
                scopeKey: this.scopeKey, nextSequence: 0, acknowledgedThrough: -1, nextChunkIndex: 0, pendingBytesSerialized: 0,
            };
            if (!Number.isSafeInteger(state.nextSequence + serialized.length)) {
                throw new ReplayOutboxError('integrity', 'Replay sequence capacity exceeded.');
            }
            const firstSequence = state.nextSequence;
            // Older rows have no counter. Initialize once from their retained
            // headers; later appends and acknowledgements update it atomically.
            const pendingBytesSerialized = state.pendingBytesSerialized ?? (await this.db.headers.where('[scopeKey+sequence]').between(
                [this.scopeKey, state.acknowledgedThrough + 1], [this.scopeKey, Number.MAX_SAFE_INTEGER], true, true,
            ).toArray()).reduce((n, header) => n + header.bytesSerialized, 0);
            const headers = serialized.map((event, index) => ({
                scopeKey: this.scopeKey, sequence: firstSequence + index, bytesSerialized: event.bytesSerialized,
                timestamp: (() => {
                    const value = events[index] as {timestamp?: unknown} | null;
                    return value && Number.isSafeInteger(value.timestamp) && Number(value.timestamp) >= 0
                        ? Number(value.timestamp) : null;
                })(),
            }));
            await this.db.headers.bulkAdd(headers);
            await this.db.bodies.bulkAdd(serialized.map((event, index) => ({
                scopeKey: this.scopeKey, sequence: firstSequence + index, json: event.json,
            })));
            await this.db.sessions.put({...state, nextSequence: firstSequence + serialized.length,
                pendingBytesSerialized: pendingBytesSerialized + serialized.reduce((n, event) => n + event.bytesSerialized, 0)});
            return {firstSequence, lastSequence: firstSequence + serialized.length - 1};
        }));
    }

    /** Returns the same persisted bytes until a matching acknowledgement commits. */
    async prepare(): Promise<PreparedReplayChunk | undefined> {
        for (;;) {
            const snapshot = await this.stored(() => this.db.transaction('r',
                this.db.sessions, this.db.headers, this.db.bodies, this.db.chunks, async () => {
                    const existing = await this.db.chunks.get(this.scopeKey);
                    if (existing) return {existing};
                    const state = await this.db.sessions.get(this.scopeKey);
                    if (!state || state.acknowledgedThrough + 1 === state.nextSequence) return {};
                    // Only metadata is read ahead. Large bodies cannot multiply the target's memory bound.
                    const headers = await this.db.headers.where('[scopeKey+sequence]').between(
                        [this.scopeKey, state.acknowledgedThrough + 1], [this.scopeKey, Number.MAX_SAFE_INTEGER], true, true,
                    ).limit(REPLAY_OUTBOX_MAX_EVENTS).toArray();
                    const group = replayChunkSlotGroups(headers, state.acknowledgedThrough + 1)[0] ?? [];
                    const selected = headers.slice(0, group.length);
                    if (!selected.length) throw new ReplayOutboxError('integrity', 'Replay event metadata is missing.');
                    const bytesSerialized = selected.reduce((total, header, index) =>
                        total + header.bytesSerialized + (index ? 1 : 0), 2);
                    const bodies = await this.db.bodies.bulkGet(selected.map(header => [this.scopeKey, header.sequence]));
                    const json = '[' + bodies.map(body => {
                        if (!body) throw new ReplayOutboxError('integrity', 'Replay event body is missing.');
                        return body.json;
                    }).join(',') + ']';
                    return {state, selected, json, bytesSerialized};
                }));
            if (snapshot.existing) return publicChunk(snapshot.existing);
            if (!snapshot.state) return undefined;

            let bytes: Uint8Array<ArrayBuffer>;
            let digest: string;
            try {
                bytes = new Uint8Array(gzip(snapshot.json));
                const hash = await crypto.subtle.digest('SHA-256', bytes);
                digest = Array.from(new Uint8Array(hash), value => value.toString(16).padStart(2, '0')).join('');
            }
            catch (cause) { throw new ReplayOutboxError('encoding', 'Replay compression or hashing failed; pending records retained.', cause); }
            if (bytes.byteLength > REPLAY_OUTBOX_MAX_UPLOAD_BYTES) {
                throw new ReplayOutboxError('chunk-too-large', 'Compressed replay exceeds the 64 MiB upload limit; pending records retained.');
            }
            const candidate: ChunkRow = {
                scopeKey: this.scopeKey, protocolVersion: 2, sessionId: this.sessionId,
                chunkIndex: snapshot.state.nextChunkIndex, digest, eventCount: snapshot.selected.length,
                firstSequence: snapshot.selected[0].sequence,
                lastSequence: snapshot.selected[snapshot.selected.length - 1].sequence,
                startTimestamp: (() => {
                    const timestamps = snapshot.selected.map(header => header.timestamp)
                        .filter((value): value is number => Number.isSafeInteger(value));
                    return timestamps.length ? Math.min(...timestamps) : null;
                })(),
                endTimestamp: (() => {
                    const timestamps = snapshot.selected.map(header => header.timestamp)
                        .filter((value): value is number => Number.isSafeInteger(value));
                    return timestamps.length ? Math.max(...timestamps) : null;
                })(),
                bytesSerialized: snapshot.bytesSerialized, bytesCompressed: bytes.byteLength, bytes,
            };
            const committed = await this.stored(() => this.db.transaction('rw', this.db.sessions, this.db.chunks, async () => {
                const existing = await this.db.chunks.get(this.scopeKey);
                if (existing) return existing;
                const state = await this.db.sessions.get(this.scopeKey);
                if (!state || state.acknowledgedThrough !== snapshot.state.acknowledgedThrough
                    || state.nextChunkIndex !== candidate.chunkIndex) return undefined;
                await this.db.chunks.add(candidate);
                return candidate;
            }));
            // A competing prepare + acknowledgement advanced the head while gzip/hash ran.
            if (committed) return publicChunk(committed);
        }
    }

    /** False means untrusted/mismatched/stale acknowledgement: no state changed. */
    async acknowledge(value: unknown): Promise<boolean> {
        if (!value || typeof value !== 'object') return false;
        const {protocolVersion, sessionId, chunkIndex, digest, eventCount, bytesCompressed} = value as Partial<ReplayChunkAcknowledgement>;
        const ack = {protocolVersion, sessionId, chunkIndex, digest, eventCount, bytesCompressed};
        if (ack.protocolVersion !== 2 || ack.sessionId !== this.sessionId
            || !Number.isSafeInteger(ack.chunkIndex) || Number(ack.chunkIndex) < 0
            || typeof ack.digest !== 'string' || !/^[a-f0-9]{64}$/.test(ack.digest)
            || !Number.isSafeInteger(ack.eventCount) || Number(ack.eventCount) < 1
            || !Number.isSafeInteger(ack.bytesCompressed) || Number(ack.bytesCompressed) < 1) return false;
        return this.stored(() => this.db.transaction('rw', this.db.sessions, this.db.headers, this.db.bodies, this.db.chunks, async () => {
            const chunk = await this.db.chunks.get(this.scopeKey);
            const state = await this.db.sessions.get(this.scopeKey);
            if (!chunk || !state || chunk.chunkIndex !== ack.chunkIndex || chunk.digest !== ack.digest
                || chunk.eventCount !== ack.eventCount || chunk.bytesCompressed !== ack.bytesCompressed
                || state.nextChunkIndex !== chunk.chunkIndex || state.acknowledgedThrough + 1 !== chunk.firstSequence) return false;
            const keys: [string, number][] = Array.from({length: chunk.eventCount}, (_, index) =>
                [this.scopeKey, chunk.firstSequence + index]);
            await this.db.headers.bulkDelete(keys);
            await this.db.bodies.bulkDelete(keys);
            await this.db.chunks.delete(this.scopeKey);
            await this.db.sessions.put({...state, acknowledgedThrough: chunk.lastSequence, nextChunkIndex: chunk.chunkIndex + 1,
                ...(state.pendingBytesSerialized === undefined ? {} :
                    {pendingBytesSerialized: state.pendingBytesSerialized - (chunk.bytesSerialized - chunk.eventCount - 1)})});
            return true;
        }));
    }

    async status(): Promise<{pendingEvents: number; nextChunkIndex: number}> {
        return this.stored(async () => {
            const state = await this.db.sessions.get(this.scopeKey);
            return {pendingEvents: state ? state.nextSequence - state.acknowledgedThrough - 1 : 0,
                nextChunkIndex: state?.nextChunkIndex ?? 0};
        });
    }

    async chunkSlotUsage(): Promise<{acknowledged: number; pending: number; total: number; bytesSerialized: number}> {
        return this.stored(() => this.db.transaction('r', this.db.sessions, this.db.headers, async () => {
            const state = await this.db.sessions.get(this.scopeKey);
            if (!state) return {acknowledged: 0, pending: 0, total: 0, bytesSerialized: 0};
            const pendingEvents = state.nextSequence - state.acknowledgedThrough - 1;
            if (pendingEvents === 0) return {acknowledged: state.nextChunkIndex, pending: 0, total: state.nextChunkIndex, bytesSerialized: 0};
            const retainedHeaders = state.pendingBytesSerialized === undefined ? await this.db.headers.where('[scopeKey+sequence]').between(
                [this.scopeKey, state.acknowledgedThrough + 1], [this.scopeKey, Number.MAX_SAFE_INTEGER], true, true,
            ).toArray() : undefined;
            const bytesSerialized = (state.pendingBytesSerialized ?? retainedHeaders!.reduce((n, header) => n + header.bytesSerialized, 0)) + 2 * pendingEvents;
            // One event per slot is the conservative maximum. If even that
            // cannot reach the rollover threshold, avoid enumerating headers.
            // Near the limit, use the authoritative greedy packer below.
            if (state.nextChunkIndex + pendingEvents < REPLAY_SESSION_SOFT_CHUNK_SLOTS - 1) {
                return {acknowledged: state.nextChunkIndex, pending: pendingEvents,
                    total: state.nextChunkIndex + pendingEvents, bytesSerialized};
            }
            const headers = await this.db.headers.where('[scopeKey+sequence]').between(
                [this.scopeKey, state.acknowledgedThrough + 1], [this.scopeKey, Number.MAX_SAFE_INTEGER], true, true,
            ).toArray();
            const pending = replayChunkSlotGroups(headers, state.acknowledgedThrough + 1).length;
            return {acknowledged: state.nextChunkIndex, pending, total: state.nextChunkIndex + pending, bytesSerialized};
        }));
    }

    /** Authoritative local totals for the finalization handshake. These values
     * come from the same durable sequence used for append/ACK, not a UI counter. */
    async completionState(): Promise<{pendingEvents: number; nextChunkIndex: number; totalEvents: number}> {
        return this.stored(async () => {
            const state = await this.db.sessions.get(this.scopeKey);
            return {
                pendingEvents: state ? state.nextSequence - state.acknowledgedThrough - 1 : 0,
                nextChunkIndex: state?.nextChunkIndex ?? 0,
                totalEvents: state?.nextSequence ?? 0,
            };
        });
    }

    /** Remove the terminal scope only after the server has confirmed the exact
     * durable totals. A mismatch retains every local row for investigation. */
    async destroyCompleted(expected: {chunkCount: number; totalEvents: number}): Promise<void> {
        if (!Number.isSafeInteger(expected.chunkCount) || expected.chunkCount < 0 ||
            !Number.isSafeInteger(expected.totalEvents) || expected.totalEvents < 0) {
            throw new ReplayOutboxError('integrity', 'Invalid replay completion totals.');
        }
        await this.stored(() => this.db.transaction('rw', this.db.sessions, this.db.headers, this.db.bodies, this.db.chunks, async () => {
            const state = await this.db.sessions.get(this.scopeKey);
            const chunk = await this.db.chunks.get(this.scopeKey);
            // A prior cleanup may have deleted the outbox and crashed before
            // the separate capture-state receipt was removed. Absence is the
            // successful idempotent result for any already-validated totals.
            if (!state && !chunk) return;
            const pending = state ? state.nextSequence - state.acknowledgedThrough - 1 : 0;
            if (!state || chunk || pending !== 0 || state.nextChunkIndex !== expected.chunkCount ||
                state.nextSequence !== expected.totalEvents) {
                throw new ReplayOutboxError('integrity', 'Replay completion does not match retained local state.');
            }
            await this.db.headers.where('[scopeKey+sequence]').between(
                [this.scopeKey, Dexie.minKey], [this.scopeKey, Dexie.maxKey], true, true,
            ).delete();
            await this.db.bodies.where('[scopeKey+sequence]').between(
                [this.scopeKey, Dexie.minKey], [this.scopeKey, Dexie.maxKey], true, true,
            ).delete();
            await this.db.sessions.delete(this.scopeKey);
        }));
    }

    /** Authoritative remote deletion permits disposal of this exact scope only. */
    async discard(): Promise<void> {
        await this.stored(() => this.db.transaction('rw', this.db.sessions, this.db.headers, this.db.bodies, this.db.chunks, async () => {
            await this.db.headers.where('[scopeKey+sequence]').between(
                [this.scopeKey, Dexie.minKey], [this.scopeKey, Dexie.maxKey], true, true,
            ).delete();
            await this.db.bodies.where('[scopeKey+sequence]').between(
                [this.scopeKey, Dexie.minKey], [this.scopeKey, Dexie.maxKey], true, true,
            ).delete();
            await this.db.chunks.delete(this.scopeKey);
            await this.db.sessions.delete(this.scopeKey);
        }));
    }
}
